import { supabase } from '../lib/supabase'

export interface UserHiddenColumns {
  id: string
  user_id: string
  hidden_columns: string[]
  created_at: string
  updated_at: string
}

// Default columns that are available for hiding
export const ALL_HIDEABLE_COLUMNS = [
  { id: 'externalColumns', label: 'Adjusted Ask / Take' },
  { id: 'relativeValue', label: 'Relative Value' },
  { id: 'owner', label: 'Owner' },
  { id: 'broker', label: 'Broker' },
  { id: 'yearMfrDlv', label: 'MAN Date / EIS Date' },
  { id: 'aftt', label: 'AFTT' },
  { id: 'emp', label: 'EMP' },
  { id: 'damage', label: 'DMG' },
  { id: 'options', label: 'Options' },
  { id: 'dateListed', label: 'Date Listed' },
  { id: 'askingPrice', label: 'Asking Price' },
  { id: 'takePrice', label: 'Take Price' },
] as const

export type HideableColumnId = typeof ALL_HIDEABLE_COLUMNS[number]['id']

export class UserHiddenColumnsService {
  /**
   * Get hidden columns for the current user (global across all markets)
   */
  static async getForCurrentUser(): Promise<string[]> {
    try {
      const { data: userData } = await supabase.auth.getUser()
      if (!userData?.user?.id) {
        return []
      }

      const { data, error } = await supabase
        .from('user_hidden_columns')
        .select('hidden_columns')
        .eq('user_id', userData.user.id)
        .maybeSingle()

      if (error) {
        return []
      }

      return data?.hidden_columns || []
    } catch (error) {
      return []
    }
  }

  /**
   * Save hidden columns for the current user (global across all markets)
   */
  static async save(hiddenColumns: string[]): Promise<void> {
    try {
      const { data: userData } = await supabase.auth.getUser()
      if (!userData?.user?.id) {
        throw new Error('User not authenticated')
      }

      const { error } = await supabase
        .from('user_hidden_columns')
        .upsert(
          {
            user_id: userData.user.id,
            hidden_columns: hiddenColumns
          },
          {
            onConflict: 'user_id'
          }
        )

      if (error) {
        throw error
      }
    } catch (error) {
      throw error
    }
  }

  /**
   * Reset hidden columns to defaults (show all columns)
   */
  static async resetToDefaults(): Promise<void> {
    try {
      const { data: userData } = await supabase.auth.getUser()
      if (!userData?.user?.id) {
        throw new Error('User not authenticated')
      }

      const { error } = await supabase
        .from('user_hidden_columns')
        .delete()
        .eq('user_id', userData.user.id)

      if (error) {
        throw error
      }
    } catch (error) {
      throw error
    }
  }

  // Special ID for cell borders preference - when present in hidden_columns, borders are hidden
  static readonly CELL_BORDERS_ID = '_cellBordersHidden'

  // Special prefix for historical months preference
  static readonly HISTORICAL_MONTHS_PREFIX = '_historicalMonths:'

  // Default historical months value (matches PDF export default)
  static readonly DEFAULT_HISTORICAL_MONTHS = 12

  /**
   * Check if cell borders are shown (not in hidden_columns)
   */
  static async getCellBordersVisible(): Promise<boolean> {
    const hiddenColumns = await this.getForCurrentUser()
    return !hiddenColumns.includes(this.CELL_BORDERS_ID)
  }

  /**
   * Set cell borders visibility
   */
  static async setCellBordersVisible(visible: boolean): Promise<void> {
    const hiddenColumns = await this.getForCurrentUser()
    const hasHiddenMarker = hiddenColumns.includes(this.CELL_BORDERS_ID)

    if (visible && hasHiddenMarker) {
      // Remove the hidden marker to show borders
      const newHiddenColumns = hiddenColumns.filter(id => id !== this.CELL_BORDERS_ID)
      await this.save(newHiddenColumns)
    } else if (!visible && !hasHiddenMarker) {
      // Add the hidden marker to hide borders
      await this.save([...hiddenColumns, this.CELL_BORDERS_ID])
    }
    // If already in the desired state, do nothing
  }

  /**
   * Get historical months preference for filtering sold/removed tables
   * Returns default value if not set
   */
  static async getHistoricalMonths(): Promise<number> {
    const hiddenColumns = await this.getForCurrentUser()
    const historicalEntry = hiddenColumns.find(id => id.startsWith(this.HISTORICAL_MONTHS_PREFIX))
    if (historicalEntry) {
      const months = parseInt(historicalEntry.replace(this.HISTORICAL_MONTHS_PREFIX, ''), 10)
      if (!isNaN(months) && months >= 1 && months <= 48) {
        return months
      }
    }
    return this.DEFAULT_HISTORICAL_MONTHS
  }

  /**
   * Set historical months preference
   * @param months Number of months (1-48)
   */
  static async setHistoricalMonths(months: number): Promise<void> {
    // Clamp value to valid range
    const clampedMonths = Math.max(1, Math.min(48, months))

    const hiddenColumns = await this.getForCurrentUser()
    // Remove any existing historical months entry
    const filteredColumns = hiddenColumns.filter(id => !id.startsWith(this.HISTORICAL_MONTHS_PREFIX))
    // Add new entry
    const newEntry = `${this.HISTORICAL_MONTHS_PREFIX}${clampedMonths}`
    await this.save([...filteredColumns, newEntry])
  }

  /**
   * Get historical months from a hidden columns array (synchronous, for use with already-fetched data)
   */
  static getHistoricalMonthsFromArray(hiddenColumns: string[]): number {
    const historicalEntry = hiddenColumns.find(id => id.startsWith(this.HISTORICAL_MONTHS_PREFIX))
    if (historicalEntry) {
      const months = parseInt(historicalEntry.replace(this.HISTORICAL_MONTHS_PREFIX, ''), 10)
      if (!isNaN(months) && months >= 1 && months <= 48) {
        return months
      }
    }
    return this.DEFAULT_HISTORICAL_MONTHS
  }
}

